/**
 * **打印包的求值器** —— 把节点参数变成「编译产物」里的组件 / 页面值。
 *
 * ## 这一层在整条链上的位置
 *
 * ```text
 *   节点参数（mm）  ──求值──►  页面 / 组件值  ──输出适配器──►  RenderTree ──► ZPL/1bpp
 *        ▲                          ▲
 *     页面编辑器改的             页面编辑器**显示**的（内容优先读它）
 * ```
 *
 * `print-template-architecture.md` §5.2 有一条修正值得抄在这里：
 * 组件里那些**依赖数据**的东西（条码矩阵、格式化后的文本）只能在
 * **求值之后**才有 —— 因为 `data` 是连线来的。所以「已编码的矩阵」这类
 * 东西属于**这里的输出**，不属于文档。
 *
 * ## 两个刻意的取舍
 *
 * ① **几何不在这里再算一遍**：`box` 直接取参数（mm），mm → 设备点的换算
 *    留给输出侧（`units.ts` 那一个函数）。这里换一次、那里再换一次，
 *    就会出现「预览 799 点、打印 800 点」这种对不上的事。
 *
 * ② **缺值给默认值而不是 null**：`xMm` 读不到时用 0 —— 组件的几何是
 *    「设计期参数」，它永远该有一个数。给 null 会让下游适配器到处判空，
 *    而那种判空迟早漏一处（表现是「某个组件打印在 (0,0)」）。
 */
import type { NodeEvalApi, NodeEvaluator } from '@/node/evaluate'
import { encode, type EncodedSymbol as PrintEncodedSymbol } from './barcode'
import { normalizeJob, type PrintJob, DEFAULT_JOB } from './job'
import { DEFAULT_BACKGROUND, DEFAULT_PAPER, normalizeRotation } from './units'

/** 读一个数值参数（读不到 / 不是数 → 默认值） */
function numParam(api: NodeEvalApi, rowId: string, fallback: number): number {
  const value = api.literal(rowId)
  const asNumber = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(asNumber) ? asNumber : fallback
}

/** 读一个文本参数 */
function textParam(api: NodeEvalApi, rowId: string, fallback: string): string {
  const value = api.literal(rowId)
  return value === undefined || value === null ? fallback : String(value)
}

/**
 * 组件值里的**几何**。
 *
 * ⚠️ 它是 mm，不是点 —— 见文件头 ①。
 *
 * ⚠️ `wMm` 是**可选**的：条码 / 二维码的宽度由"编码结果的模块数 × 模块宽"
 * 决定，而模块宽要 `dpi`（在页面上）才能吸附成整数点。
 * 所以条码的值里**没有** `wMm` —— 布局期用 `barcode.ts` 的
 * `symbolWidthMm()`（唯一实现）算。消费方别自己乘。
 */
export interface PrintBoxValue {
  xMm: number
  yMm: number
  wMm?: number
  hMm: number
}

/**
 * 求值产出的**组件值**（会顺着 `items` 那条边进页面值）。
 *
 * ⚠️ `type` 用的是和节点类型**同一个词**（`text` / `box`），
 * 而不是另起一套名字：输出适配器要按它分派，两套名字迟早有一处对不上。
 */
/**
 * 文档值：**纸张在文档上**，每一页的纸张已经被文档覆盖过（见 `documentEvaluator`）。
 *
 * ⚠️ `kind` 是 `'document'`（不是 `'page'`）：输出适配器要能一眼分清
 * "这是一份活"还是"这是里面的一页"。
 */
export interface PrintDocumentValue {
  kind: 'document'
  id: string
  widthMm: number
  heightMm: number
  dpi: number
  marginMm: number
  /**
   * 纸的底色（`#rrggbb`，默认白）。
   *
   * ⚠️ **预览属性**：模拟介质颜色，**不改变打印内容**，也**不进 IR**
   * （执行侧只知道"哪里上墨"）。它在值里是因为它是文档的一部分 ——
   * 消费方别把它当成"要铺的底色"。
   */
  backgroundColor: string
  /** 这一份文档有哪些页（顺序 = 页序） */
  /** **这一份活怎么打**（份数 / 切刀 / 浓度 / 速度 / 介质 / 走纸）—— 见 `job.ts` */
  job: PrintJob
  pages: PrintPageValue[]
}

export interface PrintComponentValue {
  kind: 'component'
  id: string
  /** 组件种类（来自节点类型）—— 与 `params.ts` 的继承链是同一套名字 */
  component: 'text' | 'box' | 'barcode' | 'qrcode' | 'image'
  box: PrintBoxValue
  props: Record<string, unknown>
  /**
   * **已编码的符号**（只有条码有）。
   *
   * 这就是架构文档 §5.2 那条修正的落点：`component.barcode` 的 `data`
   * 是**输入插槽**、值来自求值，所以编码只能发生在**求值之后** ——
   * 于是编码结果跟着 ComponentValue 走，渲染端不再需要编码器。
   *
   * ⚠️ 它是**模块单位**的（1D：条的 x/w；2D：模块矩阵），不是像素、
   * 也不是设备点：点几何由布局期算（`symbolWidthMm` + `units.ts`）。
   */
  encoded?: PrintEncodedSymbol
}

/** 求值产出的**页面值** */
export interface PrintPageValue {
  kind: 'page'
  id: string
  widthMm: number
  heightMm: number
  dpi: number
  marginMm: number
  /**
   * 纸的底色（`#rrggbb`）—— **预览用**，不进 IR（见 `PrintDocumentValue`）。
   *
   * ⚠️ 独立一页的值里这是它自己的兜底；接到文档上时由文档**覆盖**
   * （和纸张尺寸同一条规矩 —— 值必须自洽）。
   */
  backgroundColor: string
  components: PrintComponentValue[]
}

function boxOf(api: NodeEvalApi): PrintBoxValue {
  return {
    xMm: numParam(api, 'xMm', 0),
    yMm: numParam(api, 'yMm', 0),
    wMm: numParam(api, 'wMm', 10),
    hMm: numParam(api, 'hMm', 10),
  }
}

/** 文本组件：内容是**输入行**（可能来自数据源），几何是参数 */
const textEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  /*
   * ⚠️ `api.input` 而不是 `api.literal`：`literal` 只看内联控件，
   * 而「内容接线」正是这个节点最有用的用法（`data.field → 文本组件`）。
   * 没连线时 `input` 自己会回退到内联控件的值（见 `evaluate.ts`）。
   */
  const text = api.input('text')
  return {
    out: {
      kind: 'component',
      id: api.model.id,
      component: 'text',
      box: boxOf(api),
      props: {
        text: text === undefined || text === null ? '' : String(text),
        sizeMm: numParam(api, 'sizeMm', 3),
        align: textParam(api, 'align', 'left'),
        vAlign: textParam(api, 'vAlign', 'top'),
        bold: api.literal('bold') === true,
        /* 0 = 自动（行距跟着字号走）—— 见 `pageModel.ts` 里那段说明 */
        lineHeightMm: numParam(api, 'lineHeightMm', 0),
        invert: api.literal('invert') === true,
        color: textParam(api, 'color', '#000000'),
        opacity: numParam(api, 'opacity', 1),
        strokeColor: textParam(api, 'strokeColor', '#000000'),
        fillColor: textParam(api, 'fillColor', '#ffffff'),
        rotationDeg: normalizeRotation(numParam(api, 'rotationDeg', 0)),
      },
    } satisfies PrintComponentValue,
  }
}

const boxEvaluator: NodeEvaluator = (api: NodeEvalApi) => ({
  out: {
    kind: 'component',
    id: api.model.id,
    component: 'box',
    box: boxOf(api),
    props: {
      lineWidthMm: numParam(api, 'lineWidthMm', 0.3),
      fill: api.literal('fill') === true,
      strokeColor: textParam(api, 'strokeColor', '#000000'),
      fillColor: textParam(api, 'fillColor', '#ffffff'),
      radiusMm: numParam(api, 'radiusMm', 0),
      invert: api.literal('invert') === true,
      rotationDeg: normalizeRotation(numParam(api, 'rotationDeg', 0)),
    },
  } satisfies PrintComponentValue,
})

/**
 * 页面：把接上来的组件**收成一张纸**。
 *
 * ⚠️ 空槽纪律（`node-packages.md` §3.5，这里踩过一次真的 bug）：
 * 变参行末尾**故意多留一个待接的空槽**（`repeaterCountFor` = 最后已连 + 2），
 * 所以「接了 2 个组件」读出来是长度 3、最后一项 `undefined`。
 * 不丢掉它的话，页面上会多出一个 `undefined` 组件 —— 而它在编辑器里
 * 表现为「空的一格」，最容易被当成渲染 bug 去查。
 *
 * ⚠️ 这里**保留顺序**（不排序、不压实）：`items.0` 是第一个 ——
 * 画布的层序（谁压着谁）由它决定，而层序是用户摆出来的东西。
 */
const pageEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const components = api
    .repeat('items')
    .filter((item): item is PrintComponentValue => Boolean(item) && typeof item === 'object')

  return {
    out: {
      kind: 'page',
      id: api.model.id,
      widthMm: numParam(api, 'widthMm', 100),
      heightMm: numParam(api, 'heightMm', 150),
      dpi: numParam(api, 'dpi', 203),
      marginMm: numParam(api, 'marginMm', 2),
      /* ⚠️ 页面节点上已经没有这几项了（纸张归文档）—— 这里是**旧文档的兜底读取** */
      backgroundColor: textParam(api, 'backgroundColor', DEFAULT_BACKGROUND),
      components,
    } satisfies PrintPageValue,
  }
}

/**
 * 条码 / 二维码：**在这里编码**（求值阶段），把模块几何放进组件值。
 *
 * ⚠️ 编码失败**抛异常**而不是返回一个"空符号"：求值层会把异常收成
 * **这个节点上的一条具体诊断**（`evaluateGraph` 的按节点 try/catch），
 * 用户看得见"到底哪里不对"（EAN-13 位数不对 / Code 39 有不支持的字符…）。
 * 返回空符号只会让条码**静静消失**，而那要等到打印出来才发现。
 */
const barcodeEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const symbology = textParam(api, 'symbology', 'code128')
  const raw = api.input('text')
  const text = raw === undefined || raw === null ? '' : String(raw)
  /*
   * ⚠️ 纠错等级要**同时**传给编码器（它改变矩阵本身）：只把它写进 `props`
   * 而用默认等级编码的话，值里的 `encoded` 和 `props.ecLevel` 会各说各话 ——
   * 纸上画的是默认等级、面板上写的是用户选的等级。
   */
  const result = encode(symbology, text, { ecLevel: textParam(api, 'ecLevel', '') })
  if (!result.ok) throw new Error(result.message)
  return {
    out: {
      kind: 'component',
      id: api.model.id,
      component: 'barcode',
      /*
       * ⚠️ 这里**没有 wMm**（见 `PrintBoxValue`）：宽度要 dpi 才能吸附成
       * 整数点，那是**页面**的属性。布局期用 `symbolWidthMm()` 算。
       */
      box: {
        xMm: numParam(api, 'xMm', 0),
        yMm: numParam(api, 'yMm', 0),
        hMm: numParam(api, 'heightMm', 12),
      },
      props: {
        text,
        symbology,
        xDimMicron: numParam(api, 'xDimMicron', 375),
        showHri: api.literal('showHri') === true,
        /* HRI 字号 0 = 自动；显式值也要进值，IR 才能按同一个数排 */
        hriSizeMm: numParam(api, 'hriSizeMm', 0),
        /* 纠错等级（一维符号没有这一行 → 空串）：它**改变编码结果**，所以必须进值 */
        ecLevel: textParam(api, 'ecLevel', ''),
        opacity: numParam(api, 'opacity', 1),
        rotationDeg: normalizeRotation(numParam(api, 'rotationDeg', 0)),
        /* 静区是规范值（符号表里），跟着值走 —— 渲染端不用再查表 */
        quietZone: result.symbol.quietZone,
      },
      encoded: result.symbol,
    } satisfies PrintComponentValue,
  }
}

/**
 * 图片：**只搬运参数**（不做二值化 —— 那是执行侧的事）。
 *
 * ⚠️ 这里**不下载图片**：求值必须是纯的、离线的、可重放的
 * （架构文档 D1：程序 + 数据 → 产物；联网取图会让"同一份文档两次求值不同"）。
 * 取图 / 二值化都在输出侧，IR 里带的是地址与阈值。
 */
const imageEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const raw = api.input('src')
  return {
    out: {
      kind: 'component',
      id: api.model.id,
      component: 'image',
      box: boxOf(api),
      props: {
        src: raw === undefined || raw === null ? '' : String(raw),
        fit: textParam(api, 'fit', 'contain'),
        threshold: numParam(api, 'threshold', 128),
        lineWidthMm: numParam(api, 'lineWidthMm', 0),
        fill: api.literal('fill') === true,
        opacity: numParam(api, 'opacity', 1),
        strokeColor: textParam(api, 'strokeColor', '#000000'),
        fillColor: textParam(api, 'fillColor', '#ffffff'),
        radiusMm: numParam(api, 'radiusMm', 0),
        rotationDeg: normalizeRotation(numParam(api, 'rotationDeg', 0)),
      },
    } satisfies PrintComponentValue,
  }
}

/**
 * 文档：纸张 + **若干页**（每页的纸张由文档统一覆盖）。
 *
 * ⚠️ 覆盖这一步放在**求值**里（不是渲染里）：值必须是自洽的 ——
 * 消费者拿到 `document.pages[i]` 就该知道那一页多大，
 * 而不是"再去问文档要一次纸张"。渲染侧再覆盖一次就会出现
 * "编辑器按文档、IR 按页面"这种各说各话。
 */
const documentEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const job = {
    copies: numParam(api, 'copies', DEFAULT_JOB.copies),
    cut: textParam(api, 'cut', DEFAULT_JOB.cut) as PrintJob['cut'],
    darkness: numParam(api, 'darkness', DEFAULT_JOB.darkness),
    speed: textParam(api, 'speed', DEFAULT_JOB.speed) as PrintJob['speed'],
    media: textParam(api, 'media', DEFAULT_JOB.media) as PrintJob['media'],
    feedMm: numParam(api, 'feedMm', DEFAULT_JOB.feedMm),
  }
  const paper = {
    widthMm: numParam(api, 'widthMm', DEFAULT_PAPER.widthMm),
    heightMm: numParam(api, 'heightMm', DEFAULT_PAPER.heightMm),
    dpi: numParam(api, 'dpi', DEFAULT_PAPER.dpi),
    marginMm: numParam(api, 'marginMm', DEFAULT_PAPER.marginMm),
    backgroundColor: textParam(api, 'backgroundColor', DEFAULT_BACKGROUND),
  }
  const pages = api
    .repeat('pages')
    .filter((item): item is PrintPageValue => Boolean(item) && typeof item === 'object')
    .map((page) => ({ ...page, ...paper }))
  return {
    out: {
      kind: 'document',
      id: api.model.id,
      ...paper,
      /* ⚠️ 过一遍 `normalizeJob`：手改 / 导入的文档里那些值不可信 */
      job: normalizeJob(job),
      pages,
    } satisfies PrintDocumentValue,
  }
}

export const PRINT_EVALUATORS: Record<string, NodeEvaluator> = {
  'print.document': documentEvaluator,
  'print.page': pageEvaluator,
  'print.component.text': textEvaluator,
  'print.component.box': boxEvaluator,
  'print.component.barcode': barcodeEvaluator,
  /*
   * ⚠️ 二维码与条码**共用同一个求值器**：它们只差"行结构"（有没有条高 /
   * HRI）和默认符号，而求值读的是 `symbology` / `text` / `xDimMicron`
   * 这几个**同名行**。给它抄一份的代价是两处会漂（改了一处忘另一处）。
   */
  'print.component.qrcode': barcodeEvaluator,
  'print.component.image': imageEvaluator,
}
